import { createContext, useContext, useState, ReactNode } from 'react';

interface AdminContextType {
  selectedMonth: string;
  selectedYear: string;
  selectedAgentFilter: string;
  setSelectedMonth: (month: string) => void;
  setSelectedYear: (year: string) => void;
  setSelectedAgentFilter: (agent: string) => void;
  monthLabel: string;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

// Helper: build UTC-safe ISO date strings to avoid local-timezone shifts
export function getDateRange(selectedMonth: string, selectedYear: string): { startDate: string; endDate: string } {
  const year = parseInt(selectedYear);
  if (selectedMonth === 'all') {
    return {
      startDate: `${year}-01-01T00:00:00.000Z`,
      endDate: `${year}-12-31T23:59:59.999Z`
    };
  }
  const month = parseInt(selectedMonth);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const mm = String(month).padStart(2, '0');
  const dd = String(lastDay).padStart(2, '0');
  return {
    startDate: `${year}-${mm}-01T00:00:00.000Z`,
    endDate: `${year}-${mm}-${dd}T23:59:59.999Z`
  };
}

// Convenience: build UTC ISO string from year/month/day/time parts
export function buildUTCDateISO(year: number, month: number, day: number, hour = 0, min = 0, sec = 0): string {
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  const hh = String(hour).padStart(2, '0');
  const mi = String(min).padStart(2, '0');
  const ss = String(sec).padStart(2, '0');
  return `${year}-${mm}-${dd}T${hh}:${mi}:${ss}.000Z`;
}

const months = [
  { value: 'all', label: 'All Months' },
  { value: '1', label: 'January' }, { value: '2', label: 'February' },
  { value: '3', label: 'March' }, { value: '4', label: 'April' },
  { value: '5', label: 'May' }, { value: '6', label: 'June' },
  { value: '7', label: 'July' }, { value: '8', label: 'August' },
  { value: '9', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

// Agent options sorted alphabetically, with "All Agents" at top
export const AGENT_OPTIONS = [
  { value: 'all', label: 'All Agents' },
  { value: 'Direct Client', label: 'Direct' },
  { value: 'INS', label: 'INS' },
  { value: 'MET', label: 'MET' },
  { value: 'PER', label: 'PER' },
  { value: 'VRG', label: 'VRG' },
];

const STORAGE_KEY = 'global_date_filter_v1';

function loadPersistedFilter(): { month: string; year: string } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const validMonths = ['all', '1','2','3','4','5','6','7','8','9','10','11','12'];
      const validYears = YEARS;
      if (validMonths.includes(parsed.month) && validYears.includes(parsed.year)) {
        return parsed;
      }
    }
  } catch {}
  return { month: 'all', year: String(new Date().getFullYear()) };
}

export function AdminProvider({ children }: { children: ReactNode }) {
  const initial = loadPersistedFilter();
  const [selectedMonth, setSelectedMonthState] = useState(initial.month);
  const [selectedYear, setSelectedYearState] = useState(initial.year);
  const [selectedAgentFilter, setSelectedAgentFilter] = useState('all');

  const setSelectedMonth = (month: string) => {
    setSelectedMonthState(month);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ month, year: selectedYear }));
  };

  const setSelectedYear = (year: string) => {
    setSelectedYearState(year);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ month: selectedMonth, year }));
  };

  const monthLabel = months.find(m => m.value === selectedMonth)?.label || '';

  return (
    <AdminContext.Provider value={{
      selectedMonth,
      selectedYear,
      selectedAgentFilter,
      setSelectedMonth,
      setSelectedYear,
      setSelectedAgentFilter,
      monthLabel,
    }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
}

export const MONTHS = months;
export const YEARS = ['2025', '2026', '2027', '2028', '2029', '2030'];
